import { MigrationsAdminPage } from "../../Migrations/MigrationsAdminPage";
import ErrorBoundary from "../../ErrorBoundary";
import { getViewAsUser } from "../../DevTools/viewAs";
import { DevUserSwitcher, DevViewAsBanner } from "../../DevTools/DevUserSwitcher";
import { MODULE_ICON_BY_KEY } from "../../DesignTab/designModuleIcons";
import { MULTIPLACER_MODULES_BY_KEY } from "@multiconsult-group/multiplacer-core";
import { MODULE_PAGES, modulePageUrl, readProjectModule } from "../../Modules/moduleNavigation";
import { AppShell, Box, Button, Flex, Group, Notification, Text, Tooltip } from "@mantine/core";
import { MdMeetingRoom } from "react-icons/md";
import { Link, Route, Routes, useLocation } from "react-router-dom";
import MultiPlacerLogo from "../../components/MultiPlacerLogo";
import classes from "./AuthenticatedPages.module.css";
import { FaExchangeAlt } from "react-icons/fa";
import { FaCube } from "react-icons/fa6";
import { useAppSelector } from "../../app/hooks";
import { ProjectPage } from "../../ProjectPage";
import { Projects } from "../../Projects";
import Extractedtrainingtata from "../../Extractectrainingdata";
import AddProject from "../../AddProject";
import { useAccessToken } from "../../useAccessToken";
import { useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { loginRequest } from "../../authConfig";
import { UserMenu } from "../../UserMenu";
import { ProjectSwitcher } from "../../ProjectSwitcher";
import { GlobalBottomProgress } from "../../components/GlobalBottomProgress";
import { FaCircleCheck, FaTriangleExclamation, FaUsers } from "react-icons/fa6";
import HealthModal from "../../HealthModal";
import { useGetAuthMeQuery } from "../../../openapi";
import { FiDatabase, FiDownload, FiSettings, FiGrid, FiRepeat } from "react-icons/fi";
import { MdFormatListBulleted } from "react-icons/md";
import { ReleaseNotesAdminPage } from "../../Releasenotes/releasenotesadmin/ReleaseNotesAdminPage";
import { ReleaseNotesPublicPage } from "../../Releasenotes/releasenotespublic/ReleaseNotesPublicPage";
import { AdminMediaAssetsPage } from "../../MediaAssets/AdminMediaAssetsPage";
import { RevitPluginPage } from "../../RevitPluginPage/RevitPluginPage";
import { selectIsExternalUser, selectIsSystemAdmin } from "../../userSelectors";
import Testpage from "../../Testpage";
import AddTrainingData from "../../AddTrainingData";
import { IS_DEV } from "../../config";
import { getApiBaseUrl } from "../../utils";
import { isProjectViewerOnlyProject } from "../../projectViewerOnlyProject";
import { DrofusDebugPage } from "../../DrofusDebug/DrofusDebugPage";

type RuntimeEnvironment = {
  nodeEnv: string;
  multiplacerDbName: string;
  isMultiplacerProdDb: boolean;
};

export function AuthenticatedPages() {
  const { instance } = useMsal();
  const { accessToken, isLoading, error } = useAccessToken();
  const [openHealthModal, setOpenHealthModal] = useState(false);
  const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironment | null>(null);
  const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
  const location = useLocation();
  const redirectedRef = useRef(false);

  const userIsAdmin = useAppSelector(selectIsSystemAdmin);

  const userIsExternal = useAppSelector(selectIsExternalUser);

  //hämta projectNo från url
  const firstRoute = location.pathname.split("/")[1];
  const firstRouteIsProjectNo = /^\d+$/.test(firstRoute);
  const sharedView = useAppSelector(state => state.viewer.activeSharedView);
  const reduxProjectNo = useAppSelector(state => state.design.projectNo);
  const sharedStateId = new URLSearchParams(location.search).get("state");
  const currentModule = sharedStateId
    ? sharedView?.id === sharedStateId && reduxProjectNo === Number(firstRoute) ? sharedView.state.context.module : undefined
    : readProjectModule(location.search);
  const ActiveModuleIcon = currentModule ? MODULE_ICON_BY_KEY[currentModule] : FiGrid;
  const modulePages = currentModule ? MODULE_PAGES[currentModule] : [];
  const isViewerOnlyProject = isProjectViewerOnlyProject(firstRoute);

  const renderModuleTab = (page: (typeof modulePages)[number]) =>
    currentModule ? (
      <Button
        key={page}
        component={Link}
        to={modulePageUrl(firstRoute, currentModule, page)}
        variant="header"
        styles={
          page === "forekomster" || page === "endringer"
            ? {
                root: { height: 44 },
                inner: {
                  height: "100%",
                  padding: 0,
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 2,
                },
                section: { margin: 0, flexShrink: 0 },
                label: {
                  height: "auto",
                  flex: "0 0 auto",
                  lineHeight: "18px",
                  overflow: "visible",
                },
              }
            : undefined
        }
        data-current-page={location.pathname.endsWith(`/${page}`)}
        leftSection={
          page === "design" ? (
            <FaCube />
          ) : page === "room" ? (
            <MdMeetingRoom />
          ) : page === "forekomster" ? (
            <MdFormatListBulleted />
          ) : (
            <FaExchangeAlt />
          )
        }
      >
        {
          {
            design: "Modell",
            room: "Rom",
            forekomster: "Forekomster",
            endringer: "Endringer",
          }[page]
        }
      </Button>
    ) : null;
  const drofusPages = modulePages.filter((page) => page === "forekomster" || page === "endringer");

  // Redirecta EN gång när vi vet att vi inte är auth
  useEffect(() => {
    if (isLoading || error || accessToken || redirectedRef.current) return;
    redirectedRef.current = true;
    // Skicka med var vi ska tillbaka efter login
    instance.loginRedirect({
      ...loginRequest,
      redirectStartPage: location.pathname + location.search,
    });
  }, [isLoading, error, accessToken, instance, location]);

  useEffect(() => {
    if (!accessToken) return;

    const controller = new AbortController();

    fetch(`${getApiBaseUrl()}/api/runtime-environment`, {
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Runtime environment failed: ${response.status}`);
        return response.json() as Promise<RuntimeEnvironment>;
      })
      .then(setRuntimeEnvironment)
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.warn("Failed to load runtime environment", err);
      });

    return () => controller.abort();
  }, [accessToken]);

  const toast = useAppSelector((state) => state.design.toast);
  const showProdDbDevWarning = IS_DEV && runtimeEnvironment?.isMultiplacerProdDb === true;

  useGetAuthMeQuery();
  if (error) return <div>Något gick fel: {error}</div>;
  if (isLoading) return <div>Laster...</div>;
  if (!accessToken) return <div>Redirectar till login…</div>;

  return (
    <>
      <GlobalBottomProgress />
      {openHealthModal === true && <HealthModal onClose={() => setOpenHealthModal(false)} />}
      <AppShell h="100dvh" header={{ height: getViewAsUser() ? 96 : 60 }}>
        {toast.isOpen && (
          <Notification
            withCloseButton={false}
            title={toast.header}
            color={toast.isSuccess ? "teal" : "red"}
            variant={toast.isSuccess ? "filled" : "filled"}
            radius="lg"
            style={{
              position: "fixed", // säkrare än absolute
              zIndex: 1000,
              bottom: 24,
              right: 24,
              width: 480, // större
              padding: 20,
              boxShadow: "0 16px 40px rgba(0,0,0,.28)",
              backgroundColor: toast.isSuccess ? "white" : "red",
            }}
            icon={
              toast.isSuccess ? (
                <FaCircleCheck />
              ) : (
                <FaTriangleExclamation /> // tydlig varningsikon
              )
            }
            // 👇 Överstyr textfärgerna så de blir vita på röd bakgrund
            styles={(theme) => ({
              root: {
                background: toast.isSuccess ? theme.white : theme.colors.red[6],
              },
              title: {
                color: toast.isSuccess ? theme.colors.gray[9] : theme.white,
                fontSize: 18,
                fontWeight: 800,
              },
              description: {
                color: toast.isSuccess ? theme.colors.gray[7] : theme.white,
                fontSize: 16,
                lineHeight: 1.5,
              },
              icon: {
                color: toast.isSuccess ? theme.colors.teal[6] : theme.white,
              },
            })}
          >
            {toast.text}
          </Notification>
        )}
        {showProdDbDevWarning && (
          <Box
            bg="red.8"
            c="white"
            px="md"
            py={8}
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1001,
              boxShadow: "0 -6px 18px rgba(0,0,0,.22)",
            }}
          >
            <Text ta="center" fw={900} size="sm" tt="uppercase">
              Varning: utvecklingsmiljön är kopplad mot prod-databasen (
              {runtimeEnvironment?.multiplacerDbName})
            </Text>
          </Box>
        )}
        <AppShell.Header bg={"dark.7"}>
          <Group justify="center" px="md" w="100%" className={classes.headerNavigation}>
            <Group
              className={classes.mainPadding}
              align="center"
              justify="space-between"
              wrap="nowrap"
            >
              <Group className={classes.group}>
                <Flex align="center" to="/" component={Link}>
                  <MultiPlacerLogo height={50} width={150} />
                </Flex>
                {firstRouteIsProjectNo && (
                  <>
                    <Box className={classes.navigationDivider} aria-hidden="true" />
                    <ProjectSwitcher width={240} />
                    <Box className={classes.navigationDivider} aria-hidden="true" />

                    {!isViewerOnlyProject && (
                      <>
                        <Tooltip
                          label={currentModule ? "Bytt modul" : "Velg modul"}
                          withArrow
                          position="bottom"
                        >
                          <Button
                            component={Link}
                            to={`/${firstRoute}/modules`}
                            variant="filled"
                            color="blue"
                            className={`${classes.navigationButton} ${classes.moduleButton}`}
                            leftSection={<ActiveModuleIcon size={16} aria-hidden="true" />}
                            rightSection={
                              currentModule ? <FiRepeat size={14} aria-hidden="true" /> : undefined
                            }
                            aria-label={
                              currentModule
                                ? `${MULTIPLACER_MODULES_BY_KEY[currentModule].title}, bytt modul`
                                : "Velg modul"
                            }
                          >
                            {currentModule
                              ? MULTIPLACER_MODULES_BY_KEY[currentModule].title
                              : "Velg modul"}
                          </Button>
                        </Tooltip>
                        <Box className={classes.navigationDivider} aria-hidden="true" />
                        {modulePages
                          .filter((page) => page !== "forekomster" && page !== "endringer")
                          .map(renderModuleTab)}
                        {drofusPages.length > 0 && (
                          <>
                            <Box className={classes.navigationDivider} aria-hidden="true" />
                            <Box
                              className={classes.drofusNavigation}
                              role="group"
                              aria-label="dRofus"
                            >
                              <Text className={classes.drofusLabel}>dRofus</Text>
                              <Group gap={0} wrap="nowrap" className={classes.drofusTabs}>
                                {drofusPages.map(renderModuleTab)}
                              </Group>
                            </Box>
                          </>
                        )}
                      </>
                    )}

                    {isViewerOnlyProject && (
                      <Button
                        leftSection={<FaCube />}
                        data-current-page={location.pathname.endsWith("/viewer")}
                        to={`/${firstRoute}/viewer`}
                        component={Link}
                        variant="header"
                        style={{ cursor: "pointer" }}
                      >
                        Viewer
                      </Button>
                    )}
                  </>
                )}
              </Group>
              <Group wrap="nowrap">
                {IS_DEV && <DevUserSwitcher />}
                {firstRouteIsProjectNo && !isViewerOnlyProject && (
                  <>
                    {!isViewerOnlyProject && userIsExternal !== true && (
                      <Button
                        leftSection={<FaUsers />}
                        data-current-page={location.pathname.endsWith("/members")}
                        to={`/${firstRoute}/members${currentModule ? `?module=${currentModule}` : ""}`}
                        component={Link}
                        variant="header"
                        style={{ cursor: "pointer" }}
                      >
                        Medlemmer
                      </Button>
                    )}

                    {!isViewerOnlyProject && (
                      <Button
                        leftSection={<FiSettings />}
                        data-current-page={location.pathname.endsWith("/settings")}
                        to={`/${firstRoute}/settings${currentModule ? `?module=${currentModule}` : ""}`}
                        component={Link}
                        variant="header"
                        style={{ cursor: "pointer" }}
                      >
                        Innstillinger
                      </Button>
                    )}
                    <Box className={classes.navigationDivider} aria-hidden="true" />
                  </>
                )}
                {IS_DEV && (
                  <Button
                    leftSection={<FiDatabase />}
                    data-current-page={location.pathname.endsWith("/drofusdebug")}
                    to={`/drofusdebug`}
                    component={Link}
                    variant="header"
                    style={{ cursor: "pointer" }}
                  >
                    dRofus debug
                  </Button>
                )}
                <Button
                  leftSection={<FiDownload />}
                  data-current-page={location.pathname.endsWith("/revitplugin")}
                  to={`/revitplugin`}
                  component={Link}
                  variant="header"
                  style={{ cursor: "pointer" }}
                >
                  Revit-plugin
                </Button>
                {account && (
                  <>
                    <Box className={classes.navigationDivider} aria-hidden="true" />
                    <UserMenu />
                  </>
                )}
              </Group>
            </Group>
          </Group>
          {IS_DEV && <DevViewAsBanner />}
        </AppShell.Header>
        <AppShell.Main h="100dvh" className="overflow-hidden">
          <Group align="flex-start" className={`${classes.mainPadding}`} h="100%">
            <ErrorBoundary resetKey={location.pathname}>
              <Routes>
                <Route path={"/addproject"} element={<AddProject />} />
                <Route path={"/"} element={<Projects />} />
                <Route path="/:projectId/*" element={<ProjectPage />} />
                <Route path={"/extracted"} element={<Extractedtrainingtata />} />
                <Route path={"/addtrainingdata"} element={<AddTrainingData />} />
                <Route path="/migrations" element={<MigrationsAdminPage />} />
                <Route path={"/releasenotesadmin"} element={<ReleaseNotesAdminPage />} />
                <Route path={"/releasenotes"} element={<ReleaseNotesPublicPage />} />
                <Route path={"/mediaassets"} element={<AdminMediaAssetsPage />} />
                <Route path={"/revitplugin"} element={<RevitPluginPage />} />
                {IS_DEV && <Route path={"/drofusdebug"} element={<DrofusDebugPage />} />}
                <Route path={"/testpage"} element={<Testpage />} />
              </Routes>
            </ErrorBoundary>
          </Group>
        </AppShell.Main>
      </AppShell>
    </>
  );
}
